// Main app — top tabs (Query / Map), persistent header showing the
// preferred-store count.  Shares one `preferred` state across views.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import type L from "leaflet";
import { QueryView, defaultFilters, hasActiveFilters, makeRowFilter } from "./query";
import { MapView, NO_PENDING, type PendingCounts } from "./map";
import {
  useCategoryBlobs,
  CategorySwitcher,
  BlobLoading,
  BlobError,
  formatAsOf,
  CATEGORY_TABS,
} from "./categories";
import type { Filters, GeoCircle } from "./types";
import { BrandIcon } from "./icons";

function FilterIcon() {
  return (
    <svg className="filter-icon" viewBox="0 0 16 12" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <line x1="0.75" y1="2" x2="15.25" y2="2" />
        <line x1="0.75" y1="6" x2="15.25" y2="6" />
        <line x1="0.75" y1="10" x2="15.25" y2="10" />
      </g>
      <circle cx="4" cy="2" r="2.5" fill="currentColor" />
      <circle cx="11" cy="6" r="2.5" fill="currentColor" />
      <circle cx="7" cy="10" r="2.5" fill="currentColor" />
    </svg>
  );
}

// Topbar "Filter N products" pill: opens the filter drawer on mobile,
// re-expands the collapsed rail on desktop. Live count + active-filter
// dot are shared between both uses.
function FilterSummaryButton({
  count,
  active,
  onClick,
}: {
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button className={"filter-summary-btn" + (active ? " has-active" : "")} onClick={onClick}>
      <FilterIcon />
      {count < 10000 && <span className="filter-word">Filter</span>}
      <span className="mono">{count.toLocaleString()}</span>
      <span>products</span>
    </button>
  );
}

function AgeGate({ onConfirm }: { onConfirm: () => void }) {
  useEffect(() => {
    document.getElementById("wb-splash-style")?.remove();
  }, []);
  const [declined, setDeclined] = useState(false);

  const confirm = () => {
    try {
      localStorage.setItem("wb.mmj.ok", "1");
    } catch {
      /* quota */
    }
    // Defer the unmount so the click event finishes bubbling before the
    // SplashModal overlay mounts at the same screen position — otherwise
    // the click bleeds through and triggers the splash's dismiss handler,
    // making the welcome message flash and disappear instantly.
    setTimeout(onConfirm, 0);
  };

  return (
    <div className="age-gate-overlay">
      <div className="age-gate-card">
        {declined ? (
          <div className="age-gate-decline">
            This site is for Pennsylvania medical marijuana patients. See the{" "}
            <a
              href="https://www.pa.gov/agencies/health/programs/medical-marijuana.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              PA medical marijuana program
            </a>
            .
          </div>
        ) : (
          <>
            <div className="age-gate-q">Do you have a PA MMJ card?</div>
            <div className="age-gate-btns">
              <button className="age-gate-btn is-yes" onClick={confirm}>
                Yes
              </button>
              <button className="age-gate-btn is-no" onClick={() => setDeclined(true)}>
                No
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Brand({ onAbout, pulse }: { onAbout?: () => void; pulse?: boolean }) {
  const tappable =
    onAbout && (window.WEEDBUD_DATA?.about_html || window.WEEDBUD_DATA?.feedback_email);
  const cls =
    "brand" + (tappable ? " brand-tappable" : "") + (tappable && pulse ? " is-pulsing" : "");
  const inner = (
    <>
      <BrandIcon />
      <div className="brand-name">
        <span className="brand-weed">Weed</span>
        <span className="brand-bud">Bud</span>
      </div>
    </>
  );
  if (!tappable) return <div className={cls}>{inner}</div>;
  return (
    <button type="button" className={cls} onClick={onAbout} aria-label="About WeedBud">
      {inner}
    </button>
  );
}

// "Inventory updated on July 20 at 1:23pm" in the viewer's local time zone.
function formatInventoryUpdated(iso: string): string | null {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString([], { month: "long", day: "numeric" });
  const time = d
    .toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    .replace(/\s+/g, "")
    .toLowerCase();
  return `Inventory updated on ${date} at ${time}`;
}

function AboutModal({ onClose }: { onClose: () => void }) {
  const { about_html, feedback_email, as_of } = window.WEEDBUD_DATA!;
  const updated = as_of ? formatInventoryUpdated(as_of) : null;

  return (
    <div className="about-overlay" onClick={onClose}>
      <div className="about-card" onClick={(e) => e.stopPropagation()}>
        <button className="about-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        {about_html ? (
          <div className="about-body" dangerouslySetInnerHTML={{ __html: about_html }} />
        ) : (
          <div className="about-body">
            <div className="about-heading-row">
              <h2>WeedBud</h2>
              {__GIT_SHA__ && <span className="about-version">{__GIT_SHA__}</span>}
            </div>
            <p>
              Pennsylvania dispensary data, consolidated for the sole benefit of state-licensed
              medical marijuana patients.
            </p>
          </div>
        )}
        {updated && <div className="about-updated">{updated}</div>}
        {feedback_email && (
          <div className="feedback-form">
            <div className="feedback-heading">All feedback welcome!</div>
            <div className="feedback-direct">
              Email us at <a href={`mailto:${feedback_email}`}>{feedback_email}</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SplashModal({ onDismiss }: { onDismiss: () => void }) {
  const { welcome_html } = window.WEEDBUD_DATA!;
  return (
    <div className="splash-overlay" onClick={onDismiss}>
      <div className="splash-card" onClick={(e) => e.stopPropagation()}>
        <div className="splash-body" dangerouslySetInnerHTML={{ __html: welcome_html ?? "" }} />
        <button className="splash-btn" onClick={onDismiss}>
          Got it
        </button>
      </div>
    </div>
  );
}

function serializeFilters(f: Filters): string {
  return JSON.stringify({
    q: f.q,
    categories: [...f.categories],
    types: [...f.types],
    brands: [...f.brands],
    oils: [...f.oils],
    formats: [...f.formats],
    weights: [...f.weights],
    solventless: f.solventless,
    concentrateForms: [...f.concentrateForms],
    edibleForms: [...f.edibleForms],
    dose: f.dose.map((v) => (isFinite(v) ? v : null)),
    flowerExclude: [...f.flowerExclude],
    otherForms: [...f.otherForms],
    // Ranges serialize as {min,max} objects, not arrays. The shape change
    // is deliberate: under the single-ended sliders the stored hi was
    // never user-set (always the era's hardcoded cap), so honoring a
    // legacy array's hi would turn stale junk into an active ceiling.
    // Per the wb.filters.v1 policy (see loadFiltersMap), old shapes are
    // discarded to defaults rather than migrated.
    thc: { min: f.thc[0], max: f.thc[1] },
    pkgThc: { min: f.pkgThc[0], max: f.pkgThc[1] },
    ppg: { min: f.ppg[0], max: f.ppg[1] },
    terpName: f.terpName,
    terp: { min: f.terp[0], max: f.terp[1] },
    addedDaysAgo: f.addedDaysAgo.map((v) => (isFinite(v) ? v : null)),
    sort: f.sort,
  });
}

function deserializeFilters(s: string): Filters {
  try {
    const raw = JSON.parse(s);
    const d = defaultFilters();
    const rangeOrDefault = (v: unknown, dflt: [number, number]): [number, number] => {
      const o = v as { min?: unknown; max?: unknown } | null;
      return o && typeof o === "object" && typeof o.min === "number" && typeof o.max === "number"
        ? [o.min, o.max]
        : dflt;
    };
    return {
      q: typeof raw.q === "string" ? raw.q : d.q,
      categories: Array.isArray(raw.categories) ? new Set<string>(raw.categories) : d.categories,
      types: Array.isArray(raw.types) ? new Set<string>(raw.types) : d.types,
      brands: Array.isArray(raw.brands) ? new Set<string>(raw.brands) : d.brands,
      oils: Array.isArray(raw.oils) ? new Set<string>(raw.oils) : d.oils,
      formats: Array.isArray(raw.formats) ? new Set<string>(raw.formats) : d.formats,
      weights: Array.isArray(raw.weights) ? new Set<number>(raw.weights) : d.weights,
      solventless: typeof raw.solventless === "boolean" ? raw.solventless : d.solventless,
      concentrateForms: Array.isArray(raw.concentrateForms)
        ? new Set<string>(raw.concentrateForms)
        : d.concentrateForms,
      edibleForms: Array.isArray(raw.edibleForms)
        ? new Set<string>(raw.edibleForms)
        : d.edibleForms,
      dose:
        Array.isArray(raw.dose) && raw.dose.length === 2
          ? (raw.dose.map((v: number | null) => (v === null ? Infinity : v)) as [number, number])
          : d.dose,
      flowerExclude: Array.isArray(raw.flowerExclude)
        ? new Set<string>(raw.flowerExclude)
        : d.flowerExclude,
      otherForms: Array.isArray(raw.otherForms) ? new Set<string>(raw.otherForms) : d.otherForms,
      // Only the {min,max} object shape is accepted (see serializeFilters):
      // legacy arrays -- whose hi was never user-set -- fall back to
      // defaults instead of resurrecting an era-specific cap as a ceiling.
      thc: rangeOrDefault(raw.thc, d.thc),
      // Snapshots written before the unit-priced tabs moved to milligrams
      // carry no pkgThc and fall back to the unbounded default -- which is
      // why it is its own key: reading the stored percent bound here would
      // turn "≥ 20%" into "≥ 20mg".
      pkgThc: rangeOrDefault(raw.pkgThc, d.pkgThc),
      ppg: rangeOrDefault(raw.ppg, d.ppg),
      terpName: typeof raw.terpName === "string" ? raw.terpName : d.terpName,
      terp: rangeOrDefault(raw.terp, d.terp),
      addedDaysAgo:
        Array.isArray(raw.addedDaysAgo) && raw.addedDaysAgo.length === 2
          ? (raw.addedDaysAgo.map((v: number | null) => (v === null ? Infinity : v)) as [
              number,
              number,
            ])
          : d.addedDaysAgo,
      sort: raw.sort && typeof raw.sort === "object" ? raw.sort : d.sort,
    };
  } catch {
    return defaultFilters();
  }
}

// wb.filters.v1 holds a Filters snapshot PER CATEGORY (Record<category,
// serializeFilters() output>), keyed by category so switching categories
// restores that category's own last filters instead of applying one
// category's selections to another's rows (a stale "edibleForms" filter, for
// instance, zeroes every flower row -- flower rows have no such field).
// The key name stays "wb.filters.v1" (no version bump): filters are
// ephemeral UI state, so a shape change (this one, or any future one) is
// handled by validating what's stored and discarding anything that doesn't
// look like the current shape, rather than climbing a suffix.
function loadFiltersMap(): Record<string, string> {
  try {
    const raw = JSON.parse(localStorage.getItem("wb.filters.v1") || "null");
    // Reject anything that isn't a plain object, and specifically the OLD
    // flat single-Filters shape (recognizable by its own "q"/"categories"
    // fields) -- a per-category map's values are themselves serialized
    // strings, never bare filter fields.
    if (raw && typeof raw === "object" && !Array.isArray(raw) && typeof raw.q !== "string") {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(raw)) {
        if (typeof v === "string") out[k] = v;
      }
      return out;
    }
  } catch {
    /* corrupt */
  }
  return {};
}

function loadCatFilters(cat: string): Filters {
  const s = loadFiltersMap()[cat];
  return s ? deserializeFilters(s) : defaultFilters();
}

function saveCatFilters(cat: string, filters: Filters): void {
  try {
    const map = loadFiltersMap();
    map[cat] = serializeFilters(filters);
    localStorage.setItem("wb.filters.v1", JSON.stringify(map));
  } catch {
    /* quota */
  }
}

function App() {
  useEffect(() => {
    document.getElementById("wb-splash-style")?.remove();
  }, []);

  // Leaflet map handle — written by MapView's onMapReady callback so tab
  // switches can call invalidateSize() without resorting to a window global.
  const leafletMapRef = useRef<L.Map | null>(null);

  // ── Category switcher / on-demand blob loading ─────────────────────────────
  const {
    active: activeCategory,
    select: selectCategoryBlob,
    status: categoryStatus,
    activeBlob,
    retry: retryCategory,
  } = useCategoryBlobs();
  const activeCategoryLabel = (
    CATEGORY_TABS.find((t) => t.key === activeCategory)?.label ?? activeCategory
  ).toLowerCase();

  // ── Mobile detection ─────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(
    () =>
      window.matchMedia("(max-width: 767px), (pointer: coarse) and (max-width: 1023px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px), (pointer: coarse) and (max-width: 1023px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  useEffect(() => {
    document.documentElement.dataset.mobile = isMobile ? "1" : "0";
  }, [isMobile]);

  // ── App state ────────────────────────────────────────────────────────────
  const [tab, setTab] = useState<"query" | "map">(() => {
    try {
      return localStorage.getItem("wb.tab.v1") === "map" ? "map" : "query";
    } catch {
      return "query";
    }
  });
  const [mobileScreen, setMobileScreen] = useState<"map" | "products">(() => {
    try {
      return localStorage.getItem("wb.screen.v1") === "products" ? "products" : "map";
    } catch {
      return "map";
    }
  });
  const [preferred, setPreferred] = useState<Set<string>>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("wb.preferred.v1") || "null");
      if (Array.isArray(stored)) return new Set<string>(stored);
    } catch {
      /* corrupt */
    }
    return new Set<string>();
  });
  const [geoCircle, setGeoCircle] = useState<GeoCircle | null>(() => {
    try {
      const s = localStorage.getItem("wb.geo.v1");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });
  const [filters, setFilters] = useState<Filters>(() => loadCatFilters(activeCategory));
  // Filter vocab (category/strain-type/brand chips, Solventless, oils,
  // formats, ...) is derived from the active blob (frontend/DATA_CONTRACT.md),
  // so a selection made in one blob can be meaningless -- or name a value
  // absent -- in the next, e.g. a "Solventless" filter picked while viewing
  // 'vape' would zero out every row after switching to 'flower' (which has
  // no solventless rows). Load THAT category's own last-saved filters (or
  // defaults, if none) rather than the leaving category's -- the persist
  // effect below already keeps every category's entry in wb.filters.v1
  // current, so this never applies a selection to rows it doesn't apply to.
  const selectCategory = (key: string) => {
    selectCategoryBlob(key);
    setFilters(loadCatFilters(key));
  };
  const [aboutOpen, setAboutOpen] = useState(false);
  const [hasSeenAbout, setHasSeenAbout] = useState(() => {
    try {
      return localStorage.getItem("wb.about.seen.v1") === "1";
    } catch {
      return false;
    }
  });
  const openAbout = () => {
    setAboutOpen(true);
    if (!hasSeenAbout) {
      try {
        localStorage.setItem("wb.about.seen.v1", "1");
      } catch {
        /* quota */
      }
      setHasSeenAbout(true);
    }
  };
  const [splashOpen, setSplashOpen] = useState(() => {
    const { welcome_html, welcome_hash } = window.WEEDBUD_DATA!;
    if (!welcome_html || !welcome_hash) return false;
    try {
      return localStorage.getItem("wb.splash.seen") !== welcome_hash;
    } catch {
      return true;
    }
  });
  const dismissSplash = () => {
    const { welcome_hash } = window.WEEDBUD_DATA!;
    try {
      if (welcome_hash) localStorage.setItem("wb.splash.seen", welcome_hash);
    } catch {
      /* quota */
    }
    setSplashOpen(false);
  };
  // Brand pulses only after the user clears the welcome modal — otherwise the
  // pulse fires while the splash is covering it.
  const brandPulse = !hasSeenAbout && !splashOpen;
  const [pendingCounts, setPendingCounts] = useState<PendingCounts>(NO_PENDING);
  const [filteredCount, setFilteredCount] = useState(0);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(
    () => localStorage.getItem("wb.rail.collapsed") === "1",
  );
  useEffect(() => {
    try {
      localStorage.setItem("wb.rail.collapsed", railCollapsed ? "1" : "0");
    } catch {
      /* quota */
    }
  }, [railCollapsed]);

  useEffect(() => {
    try {
      localStorage.setItem("wb.preferred.v1", JSON.stringify([...preferred]));
    } catch {
      /* quota */
    }
  }, [preferred]);
  useEffect(() => {
    saveCatFilters(activeCategory, filters);
  }, [filters, activeCategory]);

  // Category tab counts: what each tab WOULD show if you switched to it —
  // that category's own persisted filters (or defaults) plus the current
  // store restriction, run through the same predicate QueryView uses. The
  // build-time category_counts in the boot blob are the fallback for blobs
  // that haven't loaded yet; with every default bound at an extreme (=
  // unbounded) those equal the default-filtered count, so a fallback is
  // only stale for an unvisited category the user has saved filters for.
  // (Flower's default hides its graded and infused rows, but the boot
  // blob is always loaded, so its fallback is never shown.)
  //
  // Recomputed from `filters` (not the stored copy) for the active tab so
  // its number tracks the sliders live rather than lagging a save.
  const categoryCounts = useMemo(() => {
    const out: Record<string, number> = { ...window.WEEDBUD_DATA!.category_counts };
    for (const t of CATEGORY_TABS) {
      // Only a fully-loaded blob can be counted; mid-load and errored tabs
      // keep the build-time fallback. Reading categoryStatus here is also
      // what makes this recompute when an on-demand blob finishes loading
      // (window.WEEDBUD_BLOBS is a plain global, not reactive state).
      if (categoryStatus[t.key] !== "ready") continue;
      const rows = window.WEEDBUD_BLOBS?.[t.key]?.rows;
      if (!rows) continue;
      const f = t.key === activeCategory ? filters : loadCatFilters(t.key);
      out[t.key] = rows.filter(makeRowFilter(f, t.key, preferred)).length;
    }
    return out;
  }, [filters, activeCategory, preferred, categoryStatus]);
  useEffect(() => {
    try {
      if (geoCircle) localStorage.setItem("wb.geo.v1", JSON.stringify(geoCircle));
      else localStorage.removeItem("wb.geo.v1");
    } catch {
      /* quota */
    }
  }, [geoCircle]);
  useEffect(() => {
    try {
      localStorage.setItem("wb.tab.v1", tab);
    } catch {
      /* quota */
    }
  }, [tab]);
  useEffect(() => {
    try {
      localStorage.setItem("wb.screen.v1", mobileScreen);
    } catch {
      /* quota */
    }
  }, [mobileScreen]);

  const switchTab = (t: "query" | "map") => {
    setTab(t);
    if (t === "map") {
      // Leaflet needs a tick after the container becomes visible to measure correctly
      setTimeout(() => {
        leafletMapRef.current?.invalidateSize();
      }, 60);
    }
  };

  const goToMap = () => {
    setMobileScreen("map");
    setTimeout(() => {
      leafletMapRef.current?.invalidateSize();
    }, 60);
  };
  const goToProducts = () => setMobileScreen("products");

  const clearAll = () => {
    setPreferred(new Set<string>());
    setGeoCircle(null);
  };

  const mapControls = (
    <button
      className="btn-icon"
      onClick={clearAll}
      disabled={preferred.size === 0 && !geoCircle}
      aria-label="Clear selection"
      title="Clear selection"
      style={{ marginLeft: "auto" }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      >
        <line x1="6" y1="6" x2="14" y2="14" />
        <line x1="14" y1="6" x2="6" y2="14" />
      </svg>
    </button>
  );

  // The map's button to the products view. While the circle is being dragged
  // it counts the stores the moved circle holds; otherwise, the selection.
  const saveCount = pendingCounts.inside ?? preferred.size;
  const saveLabel = saveCount > 0 ? `Use ${saveCount} stores` : "View Products";

  // Map and QueryView are always mounted (display:none when hidden) so
  // Leaflet stays alive and filter state isn't reset on screen switches.
  const showMap = isMobile ? mobileScreen === "map" : tab === "map";
  const showProducts = isMobile ? mobileScreen === "products" : tab === "query";

  return (
    <div className="app">
      <header className="topbar">
        {isMobile ? (
          mobileScreen === "map" ? (
            <>
              <Brand onAbout={openAbout} pulse={brandPulse} />
              {mapControls}
              <button className="mobile-save-btn" onClick={goToProducts}>
                {saveLabel}
                <span aria-hidden="true">▶</span>
              </button>
            </>
          ) : (
            <>
              <Brand onAbout={openAbout} pulse={brandPulse} />
              <div className="filter-summary-center">
                <FilterSummaryButton
                  count={filteredCount}
                  active={hasActiveFilters(filters, activeCategory)}
                  onClick={() => setFilterDrawerOpen(true)}
                />
              </div>
              <button className="mobile-map-btn" onClick={goToMap}>
                Map
                {preferred.size > 0 && <span className="tab-badge">{preferred.size}</span>}
                <span aria-hidden="true">▶</span>
              </button>
            </>
          )
        ) : tab === "map" ? (
          <>
            <Brand onAbout={openAbout} pulse={brandPulse} />
            {mapControls}
            <button className="mobile-save-btn" onClick={() => switchTab("query")}>
              {saveLabel}
              <span aria-hidden="true">▶</span>
            </button>
          </>
        ) : (
          <>
            <Brand onAbout={openAbout} pulse={brandPulse} />
            <nav className="tabs">
              <button className="tab is-on" onClick={() => switchTab("query")}>
                Products
              </button>
              <button className="tab" onClick={() => switchTab("map")}>
                Map
                {preferred.size > 0 && <span className="tab-badge">{preferred.size}</span>}
              </button>
            </nav>
            {railCollapsed && (
              <div className="filter-summary-center">
                <FilterSummaryButton
                  count={filteredCount}
                  active={hasActiveFilters(filters, activeCategory)}
                  onClick={() => setRailCollapsed(false)}
                />
              </div>
            )}
            <div className="topbar-right">
              <div className="db-stats">
                <span className="db-stats-item">
                  <span className="db-stats-val mono">
                    {window.WEEDBUD_DATA!.dispensaries.length}
                  </span>
                  <span className="db-stats-key"> stores indexed at </span>
                  <span className="db-stats-val mono">
                    {activeBlob ? formatAsOf(activeBlob.as_of) : "—"}
                  </span>
                </span>
              </div>
              <div className="pending-counts-note">
                <div
                  className={"pending-line pending-add " + (pendingCounts.add > 0 ? "is-on" : "")}
                >
                  {pendingCounts.add} stores to be added
                </div>
                <div
                  className={
                    "pending-line pending-remove " + (pendingCounts.remove > 0 ? "is-on" : "")
                  }
                >
                  {pendingCounts.remove} stores to be removed
                </div>
              </div>
            </div>
          </>
        )}
      </header>

      <main className="main">
        <div
          style={{
            display: showProducts ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <CategorySwitcher
            active={activeCategory}
            select={selectCategory}
            status={categoryStatus}
            counts={categoryCounts}
          />
          {activeBlob && categoryStatus[activeCategory] === "ready" ? (
            <QueryView
              blob={activeBlob}
              preferred={preferred}
              setPreferred={setPreferred}
              filters={filters}
              setFilters={setFilters}
              geoCircle={geoCircle}
              isMobile={isMobile}
              filterDrawerOpen={filterDrawerOpen}
              setFilterDrawerOpen={setFilterDrawerOpen}
              railCollapsed={railCollapsed}
              setRailCollapsed={setRailCollapsed}
              onFilteredCountChange={setFilteredCount}
            />
          ) : categoryStatus[activeCategory] === "error" ? (
            <BlobError label={activeCategoryLabel} onRetry={retryCategory} />
          ) : (
            <BlobLoading label={activeCategoryLabel} />
          )}
        </div>
        <div
          style={{
            display: showMap ? "flex" : "none",
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <MapView
            preferred={preferred}
            setPreferred={setPreferred}
            geoCircle={geoCircle}
            setGeoCircle={setGeoCircle}
            onPendingChange={setPendingCounts}
            onMapReady={(m) => {
              leafletMapRef.current = m;
            }}
          />
        </div>
      </main>

      {splashOpen && <SplashModal onDismiss={dismissSplash} />}
      {aboutOpen && <AboutModal onClose={() => setAboutOpen(false)} />}
    </div>
  );
}

function Root() {
  const [mmjOk, setMmjOk] = useState(() => {
    try {
      return localStorage.getItem("wb.mmj.ok") === "1";
    } catch {
      return false;
    }
  });
  if (!mmjOk) return <AgeGate onConfirm={() => setMmjOk(true)} />;
  if (!window.WEEDBUD_DATA)
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          color: "#888",
          fontFamily: "sans-serif",
          fontSize: "14px",
        }}
      >
        Data unavailable — try again later.
      </div>
    );
  return <App />;
}

// Register the service worker from the bundle rather than an inline <script>
// in index.html, so the page's CSP can keep script-src without 'unsafe-inline'.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js").catch(() => {
    /* SW optional */
  });
}

createRoot(document.getElementById("root")!).render(<Root />);
